import React, { useRef, useEffect } from 'react';
import { gradientColors } from '@24i/nxg-core-utils';
import ActionButton from '@24i/nxg-sdk-gluons/src/components/buttons/ActionButton';
import SubactionButton from '@24i/nxg-sdk-gluons/src/components/buttons/SubactionButton';
import { overridable } from '@24i/nxg-sdk-gluons/src/context/ComponentOverrides';
import { useTheme } from '@24i/nxg-sdk-higgs';
import { ImageBackground, Interactable, View, Icon } from '@24i/nxg-sdk-quarks';
import { usePlayerData } from '@24i/nxg-sdk-smartott-shared/src/context/PlayerData';
import LinearGradient from 'react-native-linear-gradient';
import { getEpgType } from '@24i/nxg-sdk-smartott/src/screens/DetailsScreen/viewModel/utils';
import { useTranslation } from 'react-i18next';
import { ELEMENT_PLACEMENT, ASSET_TYPE } from '@24i/nxg-sdk-photon/src';
import TitleView from '@24i/nxg-sdk-smartott/src/components/TitleView';
import useBlockedModal from '@24i/nxg-sdk-smartott/src/components/BlockedModal/hooks';
import { useImage } from '@24i/nxg-sdk-gluons/src/context/ImageService';
import useIsAssetPurchasedQuery from '@24i/nxg-sdk-smartott/src/hooks/query/purchases/useIsAssetPurchasedQuery';
import { BlockModalTypes } from '@24i/nxg-sdk-smartott/src/components/BlockedModal/types';
import LoadingWrapper from '@24i/nxg-sdk-smartott/src/components/LoadingWrapper';
import { Source } from '@24i/nxg-sdk-quarks/src/components/Image/types';
import {
    BANNER_CAROUSEL_TEST_IDS,
    getBannerCarouselTestID,
    getHeroBannerTestID,
    HERO_BANNER_TEST_IDS,
    getPlayButtonTestID,
} from '../../../test-utils';
import getBannerCarouselItemStyles from '../styles';
import { getPlayButtonTitleForAsset } from '../../../viewModel/handling';
import { BannerCarouselItemTypes } from '../../../types';

const BannerCarouselItem = ({
    item,
    onPress,
    onPlayPress,
    playButtonTitle,
    topGradient: propsTopGradient,
    index,
    bannerIndex,
    hideInfoButton,
    isObscured,
    setActionButtonsRef,
    showMetadata,
    isGeoBlocked,
}: BannerCarouselItemTypes) => {
    const actionButtonRef = useRef(null);
    // todo toggle color based on asset in the list or not
    const { theme } = useTheme();
    const styles = getBannerCarouselItemStyles(theme);
    const isPropsToGradientValid =
        propsTopGradient !== undefined && propsTopGradient instanceof Array;
    const bottomGradient = isPropsToGradientValid
        ? propsTopGradient.reverse()
        : gradientColors(theme.color.background1);
    const { getImageSource, getObscuredImage } = useImage();
    const { assetHasPlaybackAction } = usePlayerData();
    const { t } = useTranslation();
    const { data, isFetching: isAssetPurchasedLoading } = useIsAssetPurchasedQuery(item.data, {
        enabled: !!item.data.id,
        cacheTime: 0,
    });
    const { isPurchased } = data ?? {};
    const { openBlockedModal } = useBlockedModal();
    const hasPlaybackAction = assetHasPlaybackAction(item.data, getEpgType(item.data));

    const testIDOptions = {
        scopeIndex: bannerIndex,
        index,
    };

    const obscuredImg = getObscuredImage(item.data);

    const InfoButton = (): JSX.Element => (
        <View style={styles.primaryButtonContainer} ref={actionButtonRef}>
            <Interactable onPress={onPress}>
                <SubactionButton
                    testID={getHeroBannerTestID(HERO_BANNER_TEST_IDS.INFO_BUTTON, {
                        scopeIndex: index + 1,
                    })}
                    onPress={onPress}
                    additionalContainerStyles={styles.infoButtonContainer}
                    icon={theme.icons.packshot.moreInfo}
                    title={t('asset.info.openButton')}
                />
            </Interactable>
        </View>
    );

    const PlayButton = (): JSX.Element => (
        <View style={styles.primaryButtonContainer} ref={actionButtonRef}>
            <ActionButton
                testID={getPlayButtonTestID(item.data.type, index + 1)}
                icon={isGeoBlocked ? theme.icons.details.geoBlocked : theme.icons.packshot.play}
                additionalContainerStyles={[styles.primaryButtonContainerWithTitle]}
                onPress={
                    isGeoBlocked
                        ? () =>
                              openBlockedModal(
                                  BlockModalTypes.GEOBLOCK,
                                  t('error.E05.body') as string
                              )
                        : onPlayPress
                }
                title={
                    isGeoBlocked
                        ? t('asset.playback.unavailableButton')
                        : playButtonTitle || getPlayButtonTitleForAsset(item.data, t)
                }
            />
        </View>
    );

    useEffect(() => {
        if (!isAssetPurchasedLoading && setActionButtonsRef) {
            setActionButtonsRef(actionButtonRef);
        }
    }, []);

    const ImageBackgroundContent = (source: Source | undefined): JSX.Element => {
        let uri: string | undefined;
        if (typeof source === 'string') {
            uri = source;
            // eslint-disable-next-line react/destructuring-assignment
        } else if (typeof source === 'object' && source?.uri) {
            uri = source.uri;
        } else {
            uri = undefined;
        }

        return uri ? (
            <ImageBackground source={{ uri }} style={styles.image} resizeMode="cover" />
        ) : (
            <></>
        );
    };

    let metadata1;
    let assetTitle = item.data.title;

    if (item.data.type === ASSET_TYPE.EPISODE) {
        const { seasonNumber, episodeNumber, title } = item.data;
        assetTitle = item.data?.seriesName || item.data.podcastName;
        const seasonNumberText = seasonNumber
            ? `${t('asset.series.seasonInitial')}${seasonNumber} `
            : '';
        const episodeNumberText = episodeNumber
            ? `${t('asset.series.episode.initial')}${episodeNumber} `
            : '';
        const episodeTitleText = (seasonNumber || episodeNumber) && title ? `- ${title}` : title;

        metadata1 = `${seasonNumberText}${episodeNumberText}${episodeTitleText}`;
    }

    return (
        <View
            testID={getBannerCarouselTestID(
                BANNER_CAROUSEL_TEST_IDS.SLIDE_CONTAINER,
                testIDOptions
            )}
            style={styles.itemContainer}
        >
            <Interactable onPress={hideInfoButton ? onPress : undefined}>
                <View style={styles.textContainer}>
                    {item.title && (
                        <View>
                            <TitleView
                                isInCarousel
                                title={isObscured ? t('asset.blocked.title') : assetTitle || ''}
                                channelLogo={item.data.channelLogo}
                                startsAt={item.data?.startsAt}
                                endsAt={item.data?.endsAt}
                                subtitle={isObscured ? t('asset.blocked.metadata') : metadata1}
                                childrenPlacement={ELEMENT_PLACEMENT.BELLOW}
                                index={index}
                                assetLabel={item.data.assetLabel}
                                testID={getHeroBannerTestID(HERO_BANNER_TEST_IDS.TITLE, {
                                    scopeIndex: index + 1,
                                })}
                                showMetadata={showMetadata}
                            >
                                <LoadingWrapper
                                    isLoading={isAssetPurchasedLoading}
                                    loaderProps={{
                                        size: 'large',
                                        style: styles.activityIndicator,
                                        color: theme.color.textPrimary,
                                    }}
                                >
                                    <View style={styles.playButtonContainer}>
                                        {hasPlaybackAction && isPurchased ? (
                                            <>
                                                <PlayButton />
                                                {!hideInfoButton && <InfoButton />}
                                            </>
                                        ) : (
                                            <InfoButton />
                                        )}
                                    </View>
                                </LoadingWrapper>
                            </TitleView>
                        </View>
                    )}
                </View>
                <View style={styles.bannerItemContainer}>
                    {ImageBackgroundContent(
                        isObscured
                            ? obscuredImg
                            : { uri: item.illustration || getImageSource('fallback') }
                    )}
                    {isObscured && (
                        <>
                            <View style={styles.obscuredImageContainer}>
                                <Icon
                                    font="materialIcons"
                                    name="lock"
                                    size={89}
                                    color={theme.color.lighter2}
                                />
                            </View>
                        </>
                    )}
                    <LinearGradient colors={bottomGradient} style={styles.bottomGradient} />
                </View>
            </Interactable>
        </View>
    );
};

export default overridable(BannerCarouselItem, 'BannerCarouselItem');
